import { useState, useEffect, useCallback } from "react";
import { api, apiError } from "../lib/api";
import { fmtINR, fmtDate, daysLabel } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ReceiptText, X } from "lucide-react";

const STATE_COLORS = {
  "Invoice Expected": "bg-amber-100 text-amber-700",
  "Payment Pending": "bg-blue-100 text-blue-700",
  "Payment Overdue": "bg-red-100 text-red-700",
  "Paid": "bg-emerald-100 text-emerald-700",
};

export default function IllPayments() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [state, setState] = useState("");
  const [pay, setPay] = useState(null);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    const { data } = await api.get(`/invoices${state ? `?status=${encodeURIComponent(state)}` : ""}`);
    setRows(data);
  }, [state]);
  useEffect(() => { load(); }, [load]);

  const openPay = (inv) => { setPay(inv); setForm({ payment_date: new Date().toISOString().slice(0, 10), amount: inv.invoice_amount || inv.expected_amount || "", payment_reference: "" }); };
  const submitPay = async () => {
    try {
      await api.post(`/invoices/${pay.id}/pay`, { ...form, amount: Number(form.amount) });
      toast.success("Payment recorded"); setPay(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const counts = rows.reduce((a, r) => { a[r.state] = (a[r.state] || 0) + 1; return a; }, {});
  const field = "mt-1 w-full h-10 px-3 rounded-lg border border-slate-300 outline-none text-sm";

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-head text-2xl font-extrabold flex items-center gap-2"><ReceiptText className="text-violet-700" /> ILL / Postpaid Payments</h1>
        <p className="text-slate-500 text-sm mt-1">Leased-line and postpaid billing — invoice receipt, due dates and payment tracking.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {["Invoice Expected", "Payment Pending", "Payment Overdue", "Paid"].map((s) => (
          <button key={s} onClick={() => setState(state === s ? "" : s)} data-testid={`ill-card-${s}`}
            className={`text-left rounded-xl border p-4 ${state === s ? "border-blue-400 ring-1 ring-blue-300" : "border-slate-200 bg-white hover:border-blue-300"}`}>
            <div className="font-head text-3xl font-extrabold">{counts[s] || 0}</div>
            <div className="text-xs mt-1"><span className={`px-2 py-0.5 rounded ${STATE_COLORS[s]}`}>{s}</span></div>
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 border-b text-left text-[11px] uppercase text-slate-500">
            <th className="px-4 py-3">Customer</th><th className="px-4 py-3">ISP/BSO</th><th className="px-4 py-3">Invoice #</th>
            <th className="px-4 py-3">Period</th><th className="px-4 py-3 text-right">Amount</th><th className="px-4 py-3">Due Date</th>
            <th className="px-4 py-3">Days</th><th className="px-4 py-3">State</th><th className="px-4 py-3 text-right">Action</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-100">
                <td className="px-4 py-3 font-semibold">{r.customer_name}</td>
                <td className="px-4 py-3">{r.vendor_name}</td>
                <td className="px-4 py-3 font-mono-num text-xs">{r.invoice_number || <span className="text-amber-600">not received</span>}</td>
                <td className="px-4 py-3">{r.period}</td>
                <td className="px-4 py-3 text-right font-mono-num">{fmtINR(r.invoice_amount || r.expected_amount)}</td>
                <td className="px-4 py-3 font-mono-num text-xs">{fmtDate(r.payment_due_date)}</td>
                <td className="px-4 py-3 text-xs font-mono-num">{r.paid ? "—" : daysLabel(r.days_remaining)}</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded ${STATE_COLORS[r.state]}`}>{r.state}</span></td>
                <td className="px-4 py-3 text-right">
                  {!r.paid && can("recharge") && <button onClick={() => openPay(r)} data-testid={`pay-invoice-${r.id}`} className="px-3 py-1.5 rounded-lg bg-[var(--brand)] text-white text-xs font-semibold hover:bg-[var(--brand-hover)]">Record Payment</button>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">No invoices.</td></tr>}
          </tbody>
        </table>
      </div>

      {pay && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50" onClick={() => setPay(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="pay-modal">
            <div className="flex items-center justify-between p-5 border-b"><h3 className="font-head font-bold">Record Payment — {pay.customer_name}</h3><button onClick={() => setPay(null)} className="text-slate-400"><X /></button></div>
            <div className="p-5 space-y-3">
              <div><label className="text-sm font-medium">Payment Date</label><input type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} className={field} /></div>
              <div><label className="text-sm font-medium">Amount (₹)</label><input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={field} data-testid="pay-amount" /></div>
              <div><label className="text-sm font-medium">Payment Reference</label><input value={form.payment_reference} onChange={(e) => setForm({ ...form, payment_reference: e.target.value })} className={field} /></div>
            </div>
            <div className="p-5 border-t flex justify-end gap-3"><button onClick={() => setPay(null)} className="px-4 h-10 rounded-lg border text-sm">Cancel</button><button onClick={submitPay} data-testid="pay-submit" className="px-5 h-10 rounded-lg bg-[var(--brand)] text-white text-sm font-semibold">Confirm</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
